"use client";

import { CheckCircle2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import {
  HybridTooltip,
  HybridTooltipContent,
  HybridTooltipProvider,
  HybridTooltipTrigger,
} from "@/components/ui/hybrid-tooltip";

/**
 * Per-row vote button — Figma 5462:51863 (Phase 2).
 *
 * Disabled when: isSelf, !isVotingPeriod, or !isWalletConnected.
 * Tooltip text varies by reason. Follows WithdrawStakeButton disabled-tooltip pattern:
 * wrap disabled button in <span>, HybridTooltipTrigger asChild on the span.
 *
 * AC-01 (renders), AC-02 (self-disable), AC-03 (period-disable), AC-04 (wallet-disable).
 */

interface VoteButtonProps {
  candidateAddress: string;
  isSelf: boolean;
  isVotingPeriod: boolean;
  isWalletConnected: boolean;
  /**
   * True when the current user has already voted for THIS candidate in the
   * current cycle. Switches the button label from "رأی دادن" (vote) to
   * "رأی دیگر" (another vote) — Election.sol allows accumulative votes for
   * the same candidate, so the affordance stays available, just relabeled.
   */
  hasUserVoted?: boolean;
  onClick: () => void;
}

export const VoteButton: React.FC<VoteButtonProps> = ({
  isSelf,
  isVotingPeriod,
  isWalletConnected,
  hasUserVoted = false,
  onClick,
}) => {
  const t = useTranslations("dao");
  const disabled = isSelf || !isVotingPeriod || !isWalletConnected;
  const label = hasUserVoted
    ? t("voting.button.voteAgain")
    : t("voting.button.vote");

  const tooltipText = isSelf
    ? t("voting.button.selfDisabled") // Figma 5462:51863 disabled state
    : !isVotingPeriod
      ? t("voting.button.periodDisabled") // HDocs ui-spec
      : !isWalletConnected
        ? t("voting.button.walletDisabled")
        : null;

  const button = (
    <Button
      type="button"
      variant="solid"
      colors="white"
      size="sm"
      radius="xs"
      disabled={disabled}
      onClick={onClick}
      // start = physical-RIGHT under RTL, matches Figma 5462:51786 (voted) /
      // 5462:51765 (not yet voted) where the check-circle sits on the start side
      // of the label, not the end.
      startIcon={<CheckCircle2 className="size-5" aria-hidden="true" />}
      aria-label={label}
    >
      {label}
    </Button>
  );

  if (disabled && tooltipText !== null) {
    return (
      <HybridTooltipProvider>
        <HybridTooltip>
          <HybridTooltipTrigger asChild>
            {/* inline-flex items-center keeps the disabled button vertically centered
                inside the tooltip wrapper. Without it the span reports baseline-aligned
                inline content and the button sits at the top of the row, misaligned
                with the EyeChip on the other end. */}
            <span className="inline-flex items-center">
              {button}
              {/* Visually-hidden span keeps tooltip text accessible to screen readers
                  and to test queries (screen.getByText) without hover dependency. */}
              <span className="sr-only">{tooltipText}</span>
            </span>
          </HybridTooltipTrigger>
          <HybridTooltipContent
            side="bottom"
            className="max-w-48 text-xs text-center"
          >
            {tooltipText}
          </HybridTooltipContent>
        </HybridTooltip>
      </HybridTooltipProvider>
    );
  }

  return button;
};
